import { useState, useCallback, useMemo } from 'react';

/**
 * Transform-Typen
 */
export const TRANSFORM_TYPES = {
  NONE: 'none',
  MOVE: 'move',
  COPY: 'copy',
  ROTATE: 'rotate',
  SCALE: 'scale',
  MIRROR: 'mirror',
  ARRAY_LINEAR: 'arrayLinear',   // Linear array
  ARRAY_POLAR: 'arrayPolar',     // Polar/circular array
};

/**
 * Transform-Phasen
 */
export const TRANSFORM_PHASES = {
  IDLE: 'idle',           // Keine Transformation aktiv
  SELECT_BASE: 'select_base',   // Basispunkt wählen
  SELECT_TARGET: 'select_target', // Zielpunkt wählen
  PREVIEW: 'preview',     // Vorschau anzeigen
};

/**
 * Hook für Transformationen (Move/Copy/Rotate/Scale/Mirror).
 * Implementiert Rhino-ähnlichen Workflow:
 * 1. Objekte selektieren
 * 2. Transformation wählen
 * 3. Basispunkt setzen
 * 4. Zielpunkt/Richtung setzen
 * 5. Transformation anwenden
 *
 * Supports both building points and pure geometry objects.
 */
export function useTransform({
  movePoint,
  copyPoint,
  getPoint,
  selectedPointIds = [],
  // Geometry operations (optional)
  geometryMovePoint,
  geometryGetPoint,
  geometryTransformGeometry,
  selectedGeometryPointIds = [],
  selectedGeometryCurveIds = [],
  selectedGeometrySolidIds = [],
}) {
  // Combined selection count for checking if anything is selected
  const hasSelection = selectedPointIds.length > 0 ||
    selectedGeometryPointIds.length > 0 ||
    selectedGeometryCurveIds.length > 0 ||
    selectedGeometrySolidIds.length > 0;
  // Aktive Transformation
  const [transformType, setTransformType] = useState(TRANSFORM_TYPES.NONE);
  // Aktuelle Phase
  const [phase, setPhase] = useState(TRANSFORM_PHASES.IDLE);
  // Basispunkt für Transformation
  const [basePoint, setBasePoint] = useState(null);
  // Zielpunkt/Offset
  const [targetPoint, setTargetPoint] = useState(null);
  // Vorschau-Daten
  const [previewData, setPreviewData] = useState(null);

  // Transformation starten
  const startTransform = useCallback((type) => {
    if (!hasSelection) {
      console.warn('Keine Objekte ausgewählt für Transformation');
      return false;
    }
    setTransformType(type);
    setPhase(TRANSFORM_PHASES.SELECT_BASE);
    setBasePoint(null);
    setTargetPoint(null);
    setPreviewData(null);
    return true;
  }, [hasSelection]);

  // Basispunkt setzen
  const setBase = useCallback((x, y) => {
    if (phase !== TRANSFORM_PHASES.SELECT_BASE) return;
    setBasePoint({ x, y });
    setPhase(TRANSFORM_PHASES.SELECT_TARGET);
  }, [phase]);

  // Helper function to calculate preview position for a point
  const calculatePreviewPosition = useCallback((point, offset, angle, baseDist, base) => {
    const relX = point.x - base.x;
    const relY = point.y - base.y;

    if (transformType === TRANSFORM_TYPES.MOVE || transformType === TRANSFORM_TYPES.COPY) {
      return {
        original: { x: point.x, y: point.y },
        preview: { x: point.x + offset.x, y: point.y + offset.y },
      };
    }

    if (transformType === TRANSFORM_TYPES.ROTATE) {
      const cos = Math.cos(angle);
      const sin = Math.sin(angle);
      return {
        original: { x: point.x, y: point.y },
        preview: {
          x: base.x + relX * cos - relY * sin,
          y: base.y + relX * sin + relY * cos,
        },
        angle: angle * (180 / Math.PI),
      };
    }

    if (transformType === TRANSFORM_TYPES.SCALE) {
      const scaleFactor = baseDist / 1000;
      return {
        original: { x: point.x, y: point.y },
        preview: {
          x: base.x + relX * scaleFactor,
          y: base.y + relY * scaleFactor,
        },
        scale: scaleFactor,
      };
    }

    if (transformType === TRANSFORM_TYPES.MIRROR) {
      const lineLen = Math.sqrt(offset.x * offset.x + offset.y * offset.y) || 1;
      const nx = offset.x / lineLen;
      const ny = offset.y / lineLen;
      const dot = relX * nx + relY * ny;
      return {
        original: { x: point.x, y: point.y },
        preview: {
          x: base.x + 2 * dot * nx - relX,
          y: base.y + 2 * dot * ny - relY,
        },
      };
    }

    return null;
  }, [transformType]);

  // Vorschau aktualisieren (während Mausbewegung)
  const updatePreview = useCallback((x, y) => {
    if (phase !== TRANSFORM_PHASES.SELECT_TARGET || !basePoint) return;

    const offset = {
      x: x - basePoint.x,
      y: y - basePoint.y,
    };

    // Winkel für Rotation berechnen
    const angle = Math.atan2(offset.y, offset.x);
    // Skalierungsfaktor berechnen (Distanz vom Basispunkt)
    const baseDist = Math.sqrt(offset.x * offset.x + offset.y * offset.y);

    // Vorschau-Positionen berechnen für Building Points
    const buildingPreview = selectedPointIds.map(id => {
      const point = getPoint?.(id);
      if (!point) return null;
      const previewPos = calculatePreviewPosition(point, offset, angle, baseDist, basePoint);
      return previewPos ? { id, type: 'buildingPoint', ...previewPos } : null;
    }).filter(Boolean);

    // Vorschau-Positionen berechnen für Geometry Points
    const geometryPointPreview = selectedGeometryPointIds.map(id => {
      const point = geometryGetPoint?.(id);
      if (!point) return null;
      const previewPos = calculatePreviewPosition(point, offset, angle, baseDist, basePoint);
      return previewPos ? { id, type: 'geometryPoint', ...previewPos } : null;
    }).filter(Boolean);

    // Combine all previews
    const allPreviews = [...buildingPreview, ...geometryPointPreview];

    setPreviewData(allPreviews);
    setTargetPoint({ x, y });
  }, [phase, basePoint, selectedPointIds, getPoint, selectedGeometryPointIds, geometryGetPoint, calculatePreviewPosition]);

  // Helper function to apply transform to a single point
  const applyTransformToPoint = useCallback((point, offset, angle, scaleFactor, nx, ny, base) => {
    const relX = point.x - base.x;
    const relY = point.y - base.y;

    if (transformType === TRANSFORM_TYPES.MOVE || transformType === TRANSFORM_TYPES.COPY) {
      return { x: point.x + offset.x, y: point.y + offset.y };
    }

    if (transformType === TRANSFORM_TYPES.ROTATE) {
      const cos = Math.cos(angle);
      const sin = Math.sin(angle);
      return {
        x: base.x + relX * cos - relY * sin,
        y: base.y + relX * sin + relY * cos,
      };
    }

    if (transformType === TRANSFORM_TYPES.SCALE) {
      return {
        x: base.x + relX * scaleFactor,
        y: base.y + relY * scaleFactor,
      };
    }

    if (transformType === TRANSFORM_TYPES.MIRROR) {
      const dot = relX * nx + relY * ny;
      return {
        x: base.x + 2 * dot * nx - relX,
        y: base.y + 2 * dot * ny - relY,
      };
    }

    return { x: point.x, y: point.y };
  }, [transformType]);

  // Transformation anwenden
  const applyTransform = useCallback(() => {
    if (!basePoint || !targetPoint || !previewData) return false;

    const offset = {
      x: targetPoint.x - basePoint.x,
      y: targetPoint.y - basePoint.y,
    };

    // Berechne Transformationsparameter
    const angle = Math.atan2(offset.y, offset.x);
    const baseDist = Math.sqrt(offset.x * offset.x + offset.y * offset.y);
    const scaleFactor = baseDist / 1000;
    const lineLen = Math.sqrt(offset.x * offset.x + offset.y * offset.y) || 1;
    const nx = offset.x / lineLen;
    const ny = offset.y / lineLen;

    const isCopy = transformType === TRANSFORM_TYPES.COPY;
    const isMirror = transformType === TRANSFORM_TYPES.MIRROR;

    // Apply transform to building points
    if (selectedPointIds.length > 0) {
      if (isCopy && copyPoint) {
        selectedPointIds.forEach(id => {
          const point = getPoint?.(id);
          if (point) {
            const newPos = applyTransformToPoint(point, offset, angle, scaleFactor, nx, ny, basePoint);
            copyPoint(newPos.x, newPos.y, point.z);
          }
        });
      } else if (isMirror) {
        // Mirror creates copies by default
        if (copyPoint) {
          selectedPointIds.forEach(id => {
            const point = getPoint?.(id);
            if (point) {
              const newPos = applyTransformToPoint(point, offset, angle, scaleFactor, nx, ny, basePoint);
              copyPoint(newPos.x, newPos.y, point.z);
            }
          });
        }
      } else if (movePoint) {
        selectedPointIds.forEach(id => {
          const point = getPoint?.(id);
          if (point) {
            const newPos = applyTransformToPoint(point, offset, angle, scaleFactor, nx, ny, basePoint);
            movePoint(id, newPos.x, newPos.y);
          }
        });
      }
    }

    // Apply transform to geometry points
    if (selectedGeometryPointIds.length > 0 && geometryMovePoint) {
      selectedGeometryPointIds.forEach(id => {
        const point = geometryGetPoint?.(id);
        if (point) {
          const newPos = applyTransformToPoint(point, offset, angle, scaleFactor, nx, ny, basePoint);
          geometryMovePoint(id, newPos.x, newPos.y, point.z);
        }
      });
    }

    // Apply transform to geometry curves and solids using transformGeometry
    if (geometryTransformGeometry) {
      const allGeometryIds = [...selectedGeometryCurveIds, ...selectedGeometrySolidIds];
      if (allGeometryIds.length > 0) {
        const transformParams = {
          translate: transformType === TRANSFORM_TYPES.MOVE || transformType === TRANSFORM_TYPES.COPY
            ? { x: offset.x, y: offset.y, z: 0 }
            : undefined,
          rotate: transformType === TRANSFORM_TYPES.ROTATE ? angle : undefined,
          scale: transformType === TRANSFORM_TYPES.SCALE ? scaleFactor : undefined,
          mirror: transformType === TRANSFORM_TYPES.MIRROR
            ? { x: nx, y: ny, z: 0 }
            : undefined,
        };
        geometryTransformGeometry(allGeometryIds, transformParams, isCopy || isMirror);
      }
    }

    // Reset
    cancelTransform();
    return true;
  }, [
    basePoint, targetPoint, previewData, transformType,
    selectedPointIds, getPoint, movePoint, copyPoint,
    selectedGeometryPointIds, geometryGetPoint, geometryMovePoint,
    selectedGeometryCurveIds, selectedGeometrySolidIds, geometryTransformGeometry,
    applyTransformToPoint
  ]);

  // Transformation abbrechen
  const cancelTransform = useCallback(() => {
    setTransformType(TRANSFORM_TYPES.NONE);
    setPhase(TRANSFORM_PHASES.IDLE);
    setBasePoint(null);
    setTargetPoint(null);
    setPreviewData(null);
  }, []);

  // Convenience-Funktionen
  const startMove = useCallback(() => startTransform(TRANSFORM_TYPES.MOVE), [startTransform]);
  const startCopy = useCallback(() => startTransform(TRANSFORM_TYPES.COPY), [startTransform]);
  const startRotate = useCallback(() => startTransform(TRANSFORM_TYPES.ROTATE), [startTransform]);
  const startScale = useCallback(() => startTransform(TRANSFORM_TYPES.SCALE), [startTransform]);
  const startMirror = useCallback(() => startTransform(TRANSFORM_TYPES.MIRROR), [startTransform]);

  // Status
  const isActive = transformType !== TRANSFORM_TYPES.NONE;
  const isSelectingBase = phase === TRANSFORM_PHASES.SELECT_BASE;
  const isSelectingTarget = phase === TRANSFORM_PHASES.SELECT_TARGET;

  // Hilfstext für StatusBar
  const statusText = useMemo(() => {
    if (!isActive) return '';
    const typeNames = {
      [TRANSFORM_TYPES.MOVE]: 'Verschieben',
      [TRANSFORM_TYPES.COPY]: 'Kopieren',
      [TRANSFORM_TYPES.ROTATE]: 'Drehen',
      [TRANSFORM_TYPES.SCALE]: 'Skalieren',
      [TRANSFORM_TYPES.MIRROR]: 'Spiegeln',
    };
    const typeName = typeNames[transformType] || '';

    if (isSelectingBase) {
      return `${typeName}: Basispunkt wählen`;
    }
    if (isSelectingTarget) {
      return `${typeName}: Zielpunkt wählen (ESC zum Abbrechen)`;
    }
    return typeName;
  }, [isActive, transformType, isSelectingBase, isSelectingTarget]);

  return {
    // State
    transformType,
    phase,
    basePoint,
    targetPoint,
    previewData,

    // Status
    isActive,
    isSelectingBase,
    isSelectingTarget,
    statusText,

    // Actions
    startTransform,
    startMove,
    startCopy,
    startRotate,
    startScale,
    startMirror,
    setBase,
    updatePreview,
    applyTransform,
    cancelTransform,

    // Constants
    TRANSFORM_TYPES,
    TRANSFORM_PHASES,
  };
}

export default useTransform;
